import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Typography, Button, message, Form } from "antd";
import { MailOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import AppConfig from "../config/AppConfig";
import { getCompanie, getQuestion } from "../../Api/Apis";
import { addEmail } from "../../Api/RegistrationApis";

const { Text, Title } = Typography;

const Companies2 = () => {
  const location = useLocation();
  const formData = location.state?.formData || {};
  const questionnaireId =
    location.state?.questionnaireId ||
    (() => {
      try {
        return (
          JSON.parse(
            localStorage.getItem("BusinessQuestionnaireId") || "null",
          ) || JSON.parse(localStorage.getItem("Step8") || "null")
        );
      } catch {
        return (
          localStorage.getItem("BusinessQuestionnaireId") ||
          localStorage.getItem("Step8")
        );
      }
    })();

  const [companies, setCompanies] = useState([]);
  const [matchingList, setMatchingList] = useState([]);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);

  const getRecipientEmail = () => {
    if (business?.email) return business.email.trim();

    if (formData?.email) return formData.email.trim();

    try {
      const ld = JSON.parse(localStorage.getItem("loginDetails") || "null");
      if (ld?.email) return ld.email.trim();
    } catch {}

    try {
      const cached = JSON.parse(
        localStorage.getItem("BusinessSignupEmail") || "null",
      );
      if (cached) return cached.trim();
    } catch {}

    return "";
  };

  // summary values for header
  const summaryFromMatches = useMemo(() => {
    if (!companies || companies.length === 0) return {};
    const first = companies[0];
    return {
      industry: Array.isArray(first.industry)
        ? first.industry.join(", ")
        : first.industry || "—",
      ageGroup: Array.isArray(first.ageGroup)
        ? first.ageGroup.join(", ")
        : first.ageGroup || "—",
      budget: Array.isArray(first.ranges)
        ? first.ranges.join(", ")
        : first.ranges || "—",
    };
  }, [companies]);

  const summary = React.useMemo(() => {
    // normalize formData shapes
    const fdIndustry = Array.isArray(formData?.industry)
      ? formData.industry.join(", ")
      : formData?.industry || formData?.industries;

    const fdAge = Array.isArray(formData?.agegroup)
      ? formData.agegroup.join(", ")
      : formData?.agegroup || formData?.ageGroup;

    const fdBudget = formData?.rangeGoals || formData?.budget;

    return {
      industry: fdIndustry || summaryFromMatches.industry || "—",
      ageGroup: fdAge || summaryFromMatches.ageGroup || "—",
      budget: fdBudget || summaryFromMatches.budget || "—",
    };
  }, [formData, summaryFromMatches]);

  useEffect(() => {
    const run = async () => {
      if (!questionnaireId) {
        message.error(
          "Missing questionnaire id. Please complete the form again.",
        );
        setLoading(false);
        return;
      }

      try {
        const businessRes = await getQuestion(
          AppConfig.entityNames.questionnaires,
          questionnaireId,
        );

        const businessData =
          businessRes?.data?.entityData ||
          businessRes?.data?.data ||
          businessRes?.data;

        setBusiness(businessData);

        const res = await getCompanie(
          AppConfig.entityNames.getCompanies,
          questionnaireId,
        );

        if (res?.success && Array.isArray(res.data)) {
          setCompanies(res.data);
          setMatchingList(res.data);
        } else {
          message.error("Failed to fetch matched companies.");
        }
      } catch (e) {
        console.error(e);
        message.error("Something went wrong.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [questionnaireId]);

  const tag = (txt, i) => (
    <Button
      key={`${txt}-${i}`}
      size="small"
      style={{
        backgroundColor: "#9baebc",
        color: "#fff",
        marginRight: 6,
        marginBottom: 6,
        border: "none",
        borderRadius: 12,
      }}
    >
      {txt}
    </Button>
  );
  const handleSubmit = async () => {
    try {
      const recipientEmail = getRecipientEmail();
      if (!recipientEmail) {
        message.warning("Missing recipient email.");
        return;
      }

      const payload = {
        entityData: {
          matchingList: matchingList.length ? matchingList : companies,
          matchEmail: recipientEmail,
          industry: formData?.industry || summary.industry || "",
          ageGroup: formData?.agegroup || [],
          rangeGoals: formData?.rangeGoals || summary.budget || "",
        },
      };

      const res = await addEmail(AppConfig.entityNames.sendMail, payload);
      if (res?.success) {
        message.success("Email sent successfully.");
      } else {
        message.error(res?.message || "Could not send email.");
      }
    } catch (e) {
      console.error("addEmail error:", e);
      message.error("Something went wrong sending your email.");
    }
  };

  // const contacts = useMemo(() => {
  //   if (!business) return [];

  //   if (Array.isArray(business.contacts)) {
  //     return business.contacts;
  //   }

  //   // single contact from business
  //   return [
  //     {
  //       businessName: business.businessName,
  //       contactName: business.contactName,
  //       phone: business.phone,
  //       email: business.email,
  //       service: business.service || [],
  //       budget: business.rangeGoals || "",
  //       industry: business.industry || "",
  //     },
  //   ];
  // }, [business]);

const contacts = useMemo(() => {
  if (!business) return [];
  if (Array.isArray(business.contacts)) {
    return business.contacts.map((c) => ({
      businessName: business.businessName,
      contactName: c.name,
      phone: c.phone,
      email: c.email,
      service: c.service || [],
      budget: business.rangeGoals || "—",
      industry: business.industry || [],
    }));
  }
  return [
    {
      businessName: business.businessName,
      contactName: business.contactName,
      phone: business.phone,
      email: business.email,
      service: business.service || [],
      budget: business.rangeGoals || "—",
      industry: business.industry || [],
    },
  ];
}, [business]);


  return (
    <div className="business-signup-container">
      <Row justify="center" style={{ marginTop: 40 }}>
        <Col xs={24} sm={20} md={16} lg={14} xl={12} xxl={24}>
          <Card style={{ borderRadius: 12, textAlign: "center" }}>
            <Title level={2} style={{ textAlign: "center" }}>
              You can now contact this business.
            </Title>
            <p style={{ color: "#666" }}>
              Payment successful. Contact details are now unlocked.
            </p>
            <Row gutter={[16, 16]} justify="center" style={{ marginTop: 30 }}>
              <Col span={8}>
                <Card bordered={false}>
                  <Text strong>Industry:</Text>
                  <div>{summary.industry}</div>
                </Card>
              </Col>
              <Col span={8}>
                <Text strong>Age Group</Text>
                <div>{summary.ageGroup}</div>
              </Col>
              <Col span={8}>
                <Text strong>Budget</Text>
                <div>{summary.budget}</div>
              </Col>
            </Row>

            <Col span={24} style={{ textAlign: "center", marginTop: 10 }}>
              <p>
                You can expect to be contacted by agencies who specialize in
                your industry.
              </p>
            </Col>

            <Col span={24} style={{ textAlign: "center" }}>
              <Button
                type="primary"
                onClick={handleSubmit}
                style={{
                  borderRadius: 50,
                  backgroundColor: "#dce7f0",
                  color: "#004b84",
                  fontWeight: "bold",
                }}
              >
                <MailOutlined /> Email me this list
              </Button>
            </Col>
          </Card>
        </Col>

        {/* CONTACT DETAILS */}
        <div style={{ marginTop: 30, textAlign: "center" }}>
          <Title level={3}>Contact Details</Title>
        </div>

        <Row gutter={[16, 16]} justify="center" style={{ marginTop: 20 }}>
          {contacts.map((contact, index) => (
            <Col key={contact.id || contact.email || `${contact.businessName}-${index}`} xs={24} sm={24} md={12} lg={12} xl={12}>
              <Card
                title={contact.businessName || "Business"}
                bordered
                hoverable
                style={{ borderRadius: 10 }}
              >
                <Text strong>Contact Person Name:</Text>
                <div>{contact.contactName || "—"}</div>               

                {Array.isArray(contact.service) &&
                  contact.service.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      {contact.service.map((area, i) => (
                        <Button
                          key={i}
                          size="small"
                          style={{
                            backgroundColor: "#9baebc",
                            color: "#fff",
                            marginRight: 6,
                            marginBottom: 6,
                            border: "none",
                            borderRadius: 12,
                          }}
                        >
                          {area}
                        </Button>
                      ))}
                    </div>
                  )}

                <Text strong style={{ display: "block", marginTop: 12 }}>
                  Phone:
                </Text>
                <div>{contact.phone || "—"}</div>

                 <Text strong>Budget:</Text>
                <div>{contact.budget || "—"}</div>

                <Text strong style={{ display: "block", marginTop: 8 }}>
                  Email:
                </Text>
                <div>{contact.email || "—"}</div>
              </Card>
            </Col>
          ))}
        </Row>
      </Row>
    </div>
  );
};

export default Companies2;
